<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { getFooter } from './api';

const { locale } = useI18n();

const { data, suspense } = useQuery({
  queryKey: ['footer', locale],
  queryFn: () => getFooter(locale.value),
});

onServerPrefetch(async () => {
  await suspense();
});

const { isProduction } = useBuildTarget();

const VFragment = resolveComponent('VFragment');
</script>

<template>
  <footer class="flex flex-col items-center">
    <AppFooterCategories
      v-if="data?.categoriesColumns?.length"
      :data="data?.categoriesColumns"
    />

    <div class="flex w-full justify-center bg-dark-blue">
      <div
        class="w-full max-w-[--max-content-width] px-[--horizontal-layout-padding] pb-6 pt-12 text-white-70 md:px-0 md:pb-8 md:pt-10 md:text-grey-600"
      >
        <BaseLogo class="mb-12 text-white md:mb-10 md:ml-5" />

        <div
          class="flex items-start gap-6 pb-10 md:flex-col md:gap-8 md:px-5 md:pb-0"
        >
          <div class="grid grow grid-cols-3 gap-6 md:grid-cols-1 md:gap-8">
            <AppFooterTopLink
              v-for="link in data?.topLinks"
              :key="link.documentId"
              :data="link"
            />
            <AppFooterHelpDesk v-if="data?.helpDesk" :data="data.helpDesk" />
          </div>

          <AppFooterSocials v-if="data?.socials?.length" :data="data.socials" />
        </div>

        <div
          class="flex items-start gap-6 border-t border-t-white-20 pb-30 pt-12 md:flex-col md:items-center md:gap-16 md:border-none md:pb-7"
        >
          <AppFooterBottomLinks
            v-if="data?.bottomLinks?.length"
            :data="data.bottomLinks"
          />

          <AppFooterMobileAppSection
            v-if="data?.mobileAppSection"
            :data="data.mobileAppSection"
          />
        </div>

        <component
          :is="isProduction ? 'div' : VFragment"
          class="flex items-center justify-between gap-6 md:flex-col md:items-start md:px-5"
          data-testid="footer-bottom-text-wrapper"
        >
          <p
            v-if="data?.bottomText"
            class="text-t5 md:text-t4-mobile"
            :class="{
              'px-5': !isProduction,
            }"
            data-testid="footer-bottom-text"
          >
            {{ data.bottomText }}
          </p>

          <LazyCounterUz v-if="isProduction" />
        </component>
      </div>
    </div>
  </footer>
</template>
